import { useState } from 'react';
import type { Contact } from '../../types/contact';

// Shared avatar renderer used on:
//   - ContactDetail (size='lg' near the name header)
//   - Contact list rows (size='sm')
//   - AccountContactsSection rows on Account detail (size='sm')
//   - ContactForm preview (size='md')
//
// If photoUrl is set the <img> loads; if it 404s / errors we fall back to
// the initials badge by tracking `loadError` in local state. This means
// the URL field can hold a pasted Drive link that's unreachable without
// breaking the whole page -- the initials show up instead.

const SIZE_CLASSES: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'w-7 h-7 text-xs',
  md: 'w-12 h-12 text-sm',
  lg: 'w-20 h-20 text-xl',
};

function initialsFor(contact: Pick<Contact, 'firstName' | 'lastName'>): string {
  const first = contact.firstName?.[0] ?? '';
  const last = contact.lastName?.[0] ?? '';
  const combined = `${first}${last}`.toUpperCase();
  return combined || '?';
}

export type ContactAvatarProps = {
  contact: Pick<Contact, 'firstName' | 'lastName' | 'photoUrl'>;
  size?: 'sm' | 'md' | 'lg';
  // When true the avatar is hidden from screen readers -- use for decorative
  // avatars that sit next to the contact's name text.
  decorative?: boolean;
};

export function ContactAvatar({ contact, size = 'md', decorative }: ContactAvatarProps) {
  const [loadError, setLoadError] = useState(false);
  const showImage = !!contact.photoUrl && !loadError;
  const sizeClass = SIZE_CLASSES[size];

  if (showImage) {
    return (
      <img
        src={contact.photoUrl!}
        alt={decorative ? '' : `${contact.firstName} avatar`}
        aria-hidden={decorative || undefined}
        className={`${sizeClass} rounded-full object-cover bg-slate-100 border border-slate-200`}
        onError={() => setLoadError(true)}
      />
    );
  }

  return (
    <div
      aria-hidden={decorative || undefined}
      className={`${sizeClass} rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-semibold select-none`}
    >
      {initialsFor(contact)}
    </div>
  );
}
